import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { post } from '../lib/request'

// 业务规则 v2 冻结 5 角色；R4 追加第 6 角色 leasing_director（招商总监）；
// R5 追加第 7 角色 leasing_manager（运营）；R15 将其中文名更正为「招商经理」；
// 追加第 8 角色 rental_consultant（租房顾问）
export type AdminRole =
  | 'super_admin'
  | 'admin'
  | 'operator'
  | 'finance'
  | 'finance_manager'
  | 'leasing_director'
  | 'leasing_manager'
  | 'rental_consultant'

export const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: '超级管理员',
  admin: '管理员',
  operator: '招商主理人',
  finance: '财务',
  finance_manager: '财务经理',
  leasing_director: '招商总监',
  leasing_manager: '招商经理',
  rental_consultant: '租房主理人',
}

export const ROLE_COLORS: Record<AdminRole, string> = {
  super_admin: '#1A873B',
  admin: '#2557A7',
  operator: '#C9A84C',
  finance: '#27AE60',
  finance_manager: '#1E8449',
  leasing_director: '#8E44AD',
  leasing_manager: '#D35400',
  rental_consultant: '#13c2c2',
}

/** 角色判定工具：当前角色是否在允许列表内 */
export const hasAnyRole = (role: string | null | undefined, ...allowed: AdminRole[]): boolean =>
  !!role && (allowed as string[]).includes(role)

export const roleLabel = (role: string | null | undefined): string =>
  (role && ROLE_LABELS[role as AdminRole]) || role || '—'

interface AdminUser {
  adminId: number
  username: string
  role: string
}

interface LoginResponse {
  accessToken: string
  refreshToken: string
  adminId: number
  username: string
  role: string
}

interface AuthState {
  token: string | null
  refreshToken: string | null
  user: AdminUser | null
  isLoggedIn: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isLoggedIn: false,

      login: async (username: string, password: string) => {
        const data = await post<LoginResponse>('/admin/auth/login', {
          username,
          password,
        })

        localStorage.setItem('admin_token', data.accessToken)

        set({
          token: data.accessToken,
          refreshToken: data.refreshToken,
          user: {
            adminId: data.adminId,
            username: data.username,
            role: data.role,
          },
          isLoggedIn: true,
        })
      },

      logout: () => {
        // 可选：调后端注销 refresh token
        const { refreshToken, user } = get()
        if (refreshToken && user) {
          post('/admin/auth/logout', { refreshToken }).catch(() => {/* ignore */})
        }

        localStorage.removeItem('admin_token')
        set({ token: null, refreshToken: null, user: null, isLoggedIn: false })
      },
    }),
    {
      name: 'admin-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isLoggedIn: state.isLoggedIn,
      }),
    }
  )
)
